import { getHashedName, getNameAccountKey, NAME_PROGRAM_ID, NameRegistryState } from "@bonfida/spl-name-service";
import type { Connection } from "@solana/web3.js";
import { PublicKey } from "@solana/web3.js";
import { BN } from "bn.js";

import { getFilteredProgramAccounts, getMultipleAccounts } from "../contexts/accounts";

export const PROGRAM_ID = new PublicKey("jCebN34bUfdeUYJT13J1yG16XWQpt5PDx6Mse9GUqhR");

export async function findOwnedNameAccountsForUser(
  connection: Connection,
  userAccount: PublicKey,
): Promise<PublicKey[]> {
  const filters = [
    {
      memcmp: {
        offset: 32,
        bytes: userAccount.toBase58(),
      },
    },
  ];
  const accounts = await getFilteredProgramAccounts(connection, NAME_PROGRAM_ID, filters);
  return accounts.map((a) => a.publicKey);
}

export async function getAccount(nameAccount: PublicKey): Promise<string> {
  const [centralState] = await PublicKey.findProgramAddress([PROGRAM_ID.toBuffer()], PROGRAM_ID);
  const hashedReverseLookup = await getHashedName(nameAccount.toBase58());
  return (await getNameAccountKey(hashedReverseLookup, centralState)).toBase58();
}

export async function performReverseLookup(connection: Connection, nameAccount: PublicKey): Promise<string> {
  const [centralState] = await PublicKey.findProgramAddress([PROGRAM_ID.toBuffer()], PROGRAM_ID);
  const hashedReverseLookup = await getHashedName(nameAccount.toBase58());
  const reverseLookupAccount = await getNameAccountKey(hashedReverseLookup, centralState);

  const name = await NameRegistryState.retrieve(connection, reverseLookupAccount);
  if (!name.data) {
    throw new Error("Could not retrieve name data");
  }
  const nameLength = new BN(name.data.slice(0, 4), "le").toNumber();
  return name.data.slice(4, 4 + nameLength).toString();
}

export type UserDomainData = {
  name: string;
  nameKey: PublicKey;
};

export const getUserDomains = async (connection: Connection, publicKey: PublicKey) => {
  const domains = await findOwnedNameAccountsForUser(connection, publicKey);
  const domainPublicKeys = await Promise.all(domains.map(getAccount));
  const userDomains = await getMultipleAccounts(connection, domainPublicKeys, "processed");
  const serializedUserDomains = userDomains.array.reduce<UserDomainData[]>((acc, { data }, index) => {
    // deserialize name data from accountInfo Buffer
    // https://github.com/Bonfida/solana-program-library/blob/master/name-service/js/src/state.ts#L26
    const nameData = data.slice(96, data.length);

    const nameLength = new BN(nameData.slice(0, 4), "le").toNumber();
    const stringifiedName = nameData.slice(4, 4 + nameLength).toString();
    const userDomainData: UserDomainData = {
      name: stringifiedName,
      nameKey: new PublicKey(userDomains.keys[index]),
    };
    return [...acc, userDomainData];
  }, []);
  return serializedUserDomains.sort((a, b) => {
    return a.name.localeCompare(b.name);
  });
};
